import IconHorizontalRebrand from '@/assets/logos/icon-horizontal-svg/icon-horizontal-rebrand.svg';
import { GET_ONBOARDING_V2_VARIANT } from '@/features/flags/legacy-flags';
import { trackScreenViewed } from '@/lib/analytics/scribe/ui';
import { Link } from '@heidi/design-system/link';
import {
  TypographyFootNote,
  TypographyH2,
  TypographyP2,
} from '@heidi/design-system/typography';
import { SandPatternBackground } from '@heidi/growth';
import { ScreenName } from '@heidi/runtime/analytics-vocabulary';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useOnboardingLocaleSync } from '../hooks/useOnboardingLocaleSync';
import { useSignUpPage } from '../hooks/useSignUpPage';
import { useTimezoneLanguageDefault } from '../hooks/useTimezoneLanguageDefault';
import { SignUpForm } from './SignUpForm';
import { signUpPageMessages } from './SignUpPage.messages';
import { TrustBadges } from './TrustBadges';

export function SignUpPage() {
  useOnboardingLocaleSync(useTimezoneLanguageDefault());

  const intl = useIntl();
  const router = useRouter();
  const {
    control,
    email,
    isEmailInvalid,
    emailConnectionId,
    postLoginRedirectUri,
    isLoading,
    isFromMobileWebview,
  } = useSignUpPage();

  // Variant is stable per session — capture once at mount so all callbacks share the same snapshot.
  const [onboardingVariant] = useState(() => GET_ONBOARDING_V2_VARIANT());

  // Fire screen_viewed once after loading resolves, tagged with the A/B variant.
  const hasTrackedScreenView = useRef(false);
  useEffect(() => {
    if (isLoading || hasTrackedScreenView.current) return;
    hasTrackedScreenView.current = true;
    trackScreenViewed({
      screen_name: ScreenName.SignupFullPageView,
      url: router.asPath,
      onboarding_variant: onboardingVariant,
    });
  }, [isLoading, router.asPath, onboardingVariant]);

  if (isLoading) {
    return (
      <div className="relative flex min-h-dvh items-center justify-center overflow-hidden">
        <SandPatternBackground />
        <div className="size-8 animate-spin rounded-full border-4 border-border border-t-text-primary" />
      </div>
    );
  }

  return (
    <div className="relative flex min-h-dvh w-full flex-col items-center justify-center overflow-hidden">
      <SandPatternBackground />
      {/* Main content */}
      <div className="relative z-10 flex w-full max-w-xl flex-col items-center gap-6 px-6 py-8">
        {/* Header */}
        <div className="flex flex-col items-center gap-6">
          <Image
            src={IconHorizontalRebrand}
            alt={intl.formatMessage(signUpPageMessages.logoAlt)}
            width={80}
            height={24}
            className="dark-invert"
          />
          <div className="flex flex-col items-center gap-1 text-center">
            <TypographyH2>
              <FormattedMessage {...signUpPageMessages.useHeidiSaveHours} />
            </TypographyH2>
            <TypographyP2 className="text-text-secondary">
              <FormattedMessage {...signUpPageMessages.signUpForFree} />
            </TypographyP2>
          </div>
        </div>

        {/* Sign up form */}
        <SignUpForm
          control={control}
          email={email}
          isEmailInvalid={isEmailInvalid}
          emailConnectionId={emailConnectionId}
          postLoginRedirectUri={postLoginRedirectUri}
          isFromMobileWebview={isFromMobileWebview}
        />

        {/* Terms */}
        <TypographyFootNote className="text-center text-text-secondary">
          <FormattedMessage
            {...signUpPageMessages.termsAgreementWithLinks}
            values={{
              usagePolicy: (chunks) => (
                <Link
                  href="https://www.heidihealth.com/en-au/legal/scribe-usage-policy"
                  target="_blank"
                  variant="inline"
                >
                  {chunks}
                </Link>
              ),
              privacyPolicy: (chunks) => (
                <Link
                  href="https://www.heidihealth.com/legal/privacy-policy"
                  target="_blank"
                  variant="inline"
                >
                  {chunks}
                </Link>
              ),
              termsOfUse: (chunks) => (
                <Link
                  href="https://www.heidihealth.com/en-au/legal/heidi-scribe-terms-of-use"
                  target="_blank"
                  variant="inline"
                >
                  {chunks}
                </Link>
              ),
            }}
          />
        </TypographyFootNote>

        {/* Trust badges */}
        <TrustBadges />
      </div>
    </div>
  );
}
